import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Background,
  BackgroundVariant,
  NodeProps,
  Handle,
  Position,
  MarkerType,
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import { FlowNode, FlowEdge } from '../data/systems';

// --- Layout ---
const NODE_WIDTH = 220;
const NODE_HEIGHT = 130;

function getLayoutedElements(
  rfNodes: Node[],
  rfEdges: Edge[]
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', ranksep: 60, nodesep: 40 });

  rfNodes.forEach(n => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  rfEdges.forEach(e => g.setEdge(e.source, e.target));

  dagre.layout(g);

  const nodes = rfNodes.map(n => {
    const pos = g.node(n.id);
    return {
      ...n,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes, edges: rfEdges };
}

// --- Custom Node ---
const ToolNode: React.FC<NodeProps> = ({ data }) => {
  const hasTool = !!data.toolSlug;

  return (
    <div
      style={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        backgroundColor: hasTool ? 'var(--color-primary)' : 'var(--color-card)',
        border: `2px solid ${hasTool ? '#C04A2F' : 'var(--color-border)'}`,
        borderRadius: '10px',
        cursor: hasTool ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '8px',
        boxSizing: 'border-box',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#8B7355', border: 'none', width: 8, height: 8 }} />

      {hasTool && (
        <img
          src={`/logos/${data.toolSlug}.png`}
          alt={data.label}
          style={{
            width: 64,
            height: 64,
            borderRadius: 10,
            objectFit: 'contain',
            backgroundColor: '#FDF8F0',
            padding: 4,
            flexShrink: 0,
          }}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      )}

      <span
        style={{
          fontSize: hasTool ? '0.9rem' : '1rem',
          fontWeight: '700',
          color: hasTool ? '#1A0F0A' : 'var(--color-text-primary)',
          textAlign: 'center',
          lineHeight: 1.2,
          fontFamily: 'var(--font-primary)',
        }}
      >
        {data.label}
      </span>

      <Handle type="source" position={Position.Bottom} style={{ background: '#8B7355', border: 'none', width: 8, height: 8 }} />
    </div>
  );
};

const nodeTypes = { toolNode: ToolNode };

// --- Main Component ---
interface FlowDiagramProps {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

const FlowDiagram: React.FC<FlowDiagramProps> = ({ nodes, edges }) => {
  const navigate = useNavigate();
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    const rfNodes: Node[] = nodes.map(n => ({
      id: n.id,
      type: 'toolNode',
      position: { x: 0, y: 0 },
      data: { label: n.label, toolSlug: n.toolSlug },
    }));

    const rfEdges: Edge[] = edges.map((e, i) => ({
      id: `e-${i}`,
      source: e.from,
      target: e.to,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#8B7355', width: 20, height: 20 },
      style: { stroke: '#8B7355', strokeWidth: 2 },
    }));

    return getLayoutedElements(rfNodes, rfEdges);
  }, [nodes, edges]);

  // Calculate canvas height based on layout
  const canvasHeight = useMemo(() => {
    if (layoutedNodes.length === 0) return 400;
    const maxY = Math.max(...layoutedNodes.map(n => n.position.y + NODE_HEIGHT));
    return Math.max(400, maxY + 80);
  }, [layoutedNodes]);

  return (
    <div style={{ width: '100%', height: canvasHeight, borderRadius: 8, overflow: 'hidden' }}>
      <ReactFlow
        nodes={layoutedNodes}
        edges={layoutedEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        onNodeClick={(_, node) => {
          if (node.data.toolSlug) navigate(`/tools/${node.data.toolSlug}`);
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
        style={{ backgroundColor: '#1A0F0A' }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1}
          color="#3D2F24"
        />
      </ReactFlow>
    </div>
  );
};

export default FlowDiagram;
